// Cart size upgrade: pick the largest available size of a cart line's product.
// Pure — no window/DOM — so it runs under `node --test`. The Rebuy glue lives in
// src/entrypoints/scripts/components/rebuy-cart-filters.js (hrSmartCart.sizeUpgrade).
// Spec: docs/superpowers/specs/2026-10-02-cart-size-upgrade-design.md

export const MIN_MULTIPLE = 2;

const ML_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*ml\b/i;

// Leading volume of a size value: "50ML BOTTLE" → 50, "1.5 ml" → 1.5.
// Multi-packs ("4 x 1.5ML") and non-sizes return null.
export function parseMl(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const match = value.match(ML_PATTERN);
    if (!match) {
        return null;
    }

    const ml = parseFloat(match[1]);
    return ml > 0 ? ml : null;
}

// The Shopify cart out of whatever Rebuy hands us. `rebuy:cart.ready/change`
// put the whole Rebuy.Cart module in `event.detail.cart`, with the Shopify cart
// one level down at `.cart`; Rebuy.Cart.getCart() returns the Shopify cart itself.
export function toShopifyCart(value) {
    if (value && Array.isArray(value.items)) {
        return value;
    }

    if (value && value.cart && Array.isArray(value.cart.items)) {
        return value.cart;
    }

    return null;
}

function variantMl(variant) {
    if (!variant) {
        return null;
    }

    const fromOption = parseMl(variant.option1);
    return fromOption !== null ? fromOption : parseMl(variant.title);
}

// Bundles, bundle children, subscriptions and free gifts never get a card.
function isLineEligible(item) {
    if (!item || typeof item !== 'object') {
        return false;
    }

    if (item.final_price === 0 || item.selling_plan_allocation || item.parent_relationship) {
        return false;
    }

    return !(Array.isArray(item.children) && item.children.length);
}

/**
 * The size to offer for one cart line, or null.
 *
 * Prices are list prices in cents from /products/{handle}.js — the current
 * variant's list price, not the (possibly discounted) line price — so the delta
 * is the honest difference between the two sizes.
 *
 * A target must be buyable: `available` in the product JSON, not flagged by the
 * opt-out (`isHidden`) and not sold out by the theme's `custom.waitlist`
 * metafield (`isSoldOut`), which the rest of the theme shows as sold out.
 *
 * What else is in the cart does not matter: the upgraded unit is always added as
 * its own line carrying SIZE_UPGRADE_PROPERTY (see buildUpgradeAdd), so it never
 * merges into — or changes — another line. `cartItems` is accepted but ignored.
 */
export function findSizeUpgrade({
    item,
    product,
    cartItems = [],
    isHidden = () => false,
    isSoldOut = () => false,
    minMultiple = MIN_MULTIPLE,
} = {}) {
    try {
        if (!isLineEligible(item) || !product || !Array.isArray(product.variants)) {
            return null;
        }

        if (Array.isArray(product.options) && product.options.length > 1) {
            return null;
        }

        const current = product.variants.find((v) => v && v.id === item.variant_id);
        let fromMl = variantMl(current);
        if (fromMl === null) {
            fromMl = parseMl(item.variant_title);
        }

        const fromPrice = current && typeof current.price === 'number' ? current.price : item.original_price;
        if (fromMl === null || typeof fromPrice !== 'number' || fromPrice <= 0) {
            return null;
        }

        let best = null;
        let bestMl = 0;

        product.variants.forEach((variant) => {
            if (!variant || variant.id === item.variant_id || !variant.available || isSoldOut(variant) || isHidden(variant)) {
                return;
            }

            const ml = variantMl(variant);
            if (ml === null || ml <= fromMl || typeof variant.price !== 'number' || variant.price <= fromPrice) {
                return;
            }

            if (!best || ml > bestMl || (ml === bestMl && variant.price < best.price)) {
                best = variant;
                bestMl = ml;
            }
        });

        if (!best) {
            return null;
        }

        // minMultiple compares the rounded value the tile displays (6.25 → "6.3×").
        const multiple = Math.round((bestMl / fromMl) * 10) / 10;

        return {
            variant: best,
            ml: bestMl,
            fromMl,
            fromPrice,
            toPrice: best.price,
            delta: best.price - fromPrice,
            multiple: multiple >= minMultiple ? multiple : null,
            label: String(bestMl) + 'ML',
            fromLabel: String(fromMl) + 'ML',
        };
    } catch (error) {
        return null; // never let bad data break the cart render
    }
}

// Line item property marking a line created by the size switch — hidden from
// shoppers (the `_` prefix: Rebuy's itemProperties() and Shopify checkout skip
// it) and readable by a discount (`line.attribute(key: "_size_upgrade")`). The
// value is the size the shopper upgraded from, e.g. "8ML BOTTLE".
export const SIZE_UPGRADE_PROPERTY = '_size_upgrade';

/**
 * The /cart/add.js body for an upgrade: the larger variant, the line's quantity,
 * and the line's own properties (keeps e.g. Rebuy attribution) plus the marker.
 * A differing property set keeps it a separate cart line from any 50ML already
 * there, so a discount on the marker only ever hits upgraded units.
 */
export function buildUpgradeAdd(item, upgrade) {
    const own = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    const quantity = item && typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;

    return {
        items: [
            {
                id: upgrade.variant.id,
                quantity,
                properties: {...own, [SIZE_UPGRADE_PROPERTY]: item.variant_title},
            },
        ],
    };
}

// ---- App-controlled settings ------------------------------------------------
// The Size Upgrade app writes the shop metafield hr_size_upgrade.settings (JSON);
// layout/theme.liquid publishes it as window.theme.sizeUpgrade. Missing or invalid
// values fall back to these defaults — today's built-in behaviour.

export const DEFAULT_SIZE_UPGRADE_SETTINGS = Object.freeze({
    enabled: true,
    excludedProductIds: Object.freeze([]),
    minMultiple: MIN_MULTIPLE,
    labels: Object.freeze({
        title: 'Upgrade to {size}',
        button: 'Upgrade',
        multiple: '{n}× more fragrance',
    }),
    discount: null,
});

// "123", 123 and "gid://shopify/Product/123" → "123"; anything else → null.
function productIdString(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(Math.trunc(value));
    }

    if (typeof value === 'string') {
        const match = value.trim().match(/(?:^|\/)(\d+)$/);
        return match ? match[1] : null;
    }

    return null;
}

function numberOr(value, fallback, min, max) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
    }

    return Math.min(max, Math.max(min, value));
}

function labelOr(value, fallback) {
    return typeof value === 'string' && value.trim() ? value : fallback;
}

/**
 * A complete, validated settings object from whatever the metafield holds (an
 * object, or a JSON string). Never throws; each field falls back on its own.
 */
export function resolveSizeUpgradeSettings(raw) {
    const defaults = DEFAULT_SIZE_UPGRADE_SETTINGS;
    let input = raw;

    if (typeof input === 'string') {
        try {
            input = JSON.parse(input);
        } catch (error) {
            input = null;
        }
    }

    if (!input || typeof input !== 'object' || Array.isArray(input)) {
        input = {};
    }

    const labels = input.labels && typeof input.labels === 'object' ? input.labels : {};

    return {
        enabled: input.enabled !== false,
        excludedProductIds: Array.isArray(input.excludedProductIds)
            ? input.excludedProductIds.map(productIdString).filter(Boolean)
            : [],
        minMultiple: numberOr(input.minMultiple, defaults.minMultiple, 1, Infinity),
        labels: {
            title: labelOr(labels.title, defaults.labels.title),
            button: labelOr(labels.button, defaults.labels.button),
            multiple: labelOr(labels.multiple, defaults.labels.multiple),
        },
        discount: resolveDiscount(input.discount),
    };
}

export function isProductExcluded(productId, settings) {
    const id = productIdString(productId);
    return Boolean(id && settings && Array.isArray(settings.excludedProductIds) && settings.excludedProductIds.includes(id));
}

/**
 * The tile card's text for one upgrade, from the settings' label templates. Plain
 * text — the template interpolates it with {{ }}, so Vue escapes it. `multiple`
 * is null when its value didn't qualify.
 */
export function buildUpgradeText(upgrade, settings) {
    const labels = (settings && settings.labels) || DEFAULT_SIZE_UPGRADE_SETTINGS.labels;
    const fill = (template, key, value) => template.split(key).join(String(value));

    return {
        title: fill(labels.title, '{size}', upgrade && upgrade.label ? upgrade.label : ''),
        button: labels.button,
        multiple:
            upgrade && upgrade.multiple !== null && upgrade.multiple !== undefined
                ? fill(labels.multiple, '{n}', upgrade.multiple)
                : null,
    };
}

// ---- Size upgrade discount ---------------------------------------------------
// The app's Discount Function discounts upgraded lines (SIZE_UPGRADE_PROPERTY) at
// checkout; its rules are mirrored in settings.discount so the tile can show the
// discounted price. `discount` is null when there is no active discount. Every
// check errs towards null: the tile must never promise a discount checkout won't apply.

const VALUE_TYPES = ['percentage', 'fixedAmount'];
const PRODUCT_SCOPES = ['all', 'products', 'collections'];

function idList(value) {
    return Array.isArray(value) ? value.map(productIdString).filter(Boolean) : [];
}

/**
 * The normalised settings.discount, or null when it is missing or unusable
 * (unknown type or scope, no positive value, no valid start date).
 */
export function resolveDiscount(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }

    if (!VALUE_TYPES.includes(raw.valueType) || !PRODUCT_SCOPES.includes(raw.productScope)) {
        return null;
    }

    if (typeof raw.value !== 'number' || !Number.isFinite(raw.value) || raw.value <= 0) {
        return null;
    }

    if (raw.valueType === 'percentage' && raw.value > 100) {
        return null;
    }

    const startsAt = typeof raw.startsAt === 'string' ? Date.parse(raw.startsAt) : NaN;
    if (Number.isNaN(startsAt)) {
        return null;
    }

    let endsAt = Infinity;
    if (raw.endsAt !== null && raw.endsAt !== undefined) {
        endsAt = typeof raw.endsAt === 'string' ? Date.parse(raw.endsAt) : NaN;
        if (Number.isNaN(endsAt)) {
            return null;
        }
    }

    const max = raw.maxDiscountedUnitsPerOrder;

    return {
        valueType: raw.valueType,
        value: raw.value,
        productScope: raw.productScope,
        productIds: idList(raw.productIds),
        collectionIds: idList(raw.collectionIds),
        maxDiscountedUnitsPerOrder: typeof max === 'number' && Number.isFinite(max) && max >= 0 ? Math.floor(max) : Infinity,
        startsAt,
        endsAt,
    };
}

// Units already upgraded in the cart (lines carrying a non-empty marker).
export function countUpgradedUnits(cartItems) {
    if (!Array.isArray(cartItems)) {
        return 0;
    }

    return cartItems.reduce((sum, line) => {
        const marker = line && line.properties && line.properties[SIZE_UPGRADE_PROPERTY];
        const quantity = line && typeof line.quantity === 'number' && line.quantity > 0 ? line.quantity : 0;
        return typeof marker === 'string' && marker.trim() ? sum + quantity : sum;
    }, 0);
}

/**
 * The discounted unit price (cents, presentment currency) of an upgrade target,
 * or null when the discount doesn't apply.
 *
 * - quantity: units this upgrade adds. The cap must cover all of them, so a
 *   2-unit line with one slot left shows the plain price (checkout would only
 *   discount one unit, but the tile prices "each").
 * - collectionIds: the target product's collection ids, or null when unknown;
 *   unknown never matches a collections scope.
 * - rate: Shopify.currency.rate, converting a fixed amount (shop currency, major
 *   units) into the presentment currency.
 */
export function getSizeUpgradeDiscountedPrice(
    targetPriceCents,
    targetProductId,
    cartItems,
    settings,
    {quantity = 1, collectionIds = null, rate = 1, now = Date.now()} = {},
) {
    try {
        const discount = settings && settings.discount;
        if (!discount || typeof targetPriceCents !== 'number' || targetPriceCents <= 0) {
            return null;
        }

        if (!(discount.startsAt <= now && now < discount.endsAt)) {
            return null;
        }

        if (discount.productScope === 'products') {
            const id = productIdString(targetProductId);
            if (!id || !discount.productIds.includes(id)) {
                return null;
            }
        } else if (discount.productScope === 'collections') {
            const ids = idList(collectionIds);
            if (!ids.some((id) => discount.collectionIds.includes(id))) {
                return null;
            }
        } else if (discount.productScope !== 'all') {
            return null;
        }

        const units = typeof quantity === 'number' && quantity > 0 ? quantity : 1;
        if (countUpgradedUnits(cartItems) + units > discount.maxDiscountedUnitsPerOrder) {
            return null;
        }

        if (discount.valueType === 'percentage') {
            return Math.round(targetPriceCents * (1 - discount.value / 100));
        }

        const fx = typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : 1;
        return Math.max(0, targetPriceCents - Math.round(discount.value * 100 * fx));
    } catch (error) {
        return null;
    }
}

// What the tile shows with the discount: discounted - current, or null when that
// isn't a positive amount below the plain delta (the tile then shows the delta).
export function getDiscountedDelta(upgrade, discountedPrice) {
    if (!upgrade || typeof discountedPrice !== 'number' || typeof upgrade.fromPrice !== 'number') {
        return null;
    }

    const delta = discountedPrice - upgrade.fromPrice;
    return delta > 0 && delta < upgrade.delta ? delta : null;
}
